import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Search,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowLeft, ShieldCheck, Key, Calendar, Info, Clock,
    ChevronUp, ChevronDown
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../../components/ItemsPerPageDropdown ";

// ── Types ─────────────────────────────────────────────────────────────────────
interface FirewallLicenseItem {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    organization_id: number;
    client_id: number;
    asset_id: number;
    agent_id: number;
    firewall_type: string;
    name: string;
    key: string;
    status: string;
    entitlement: string;
    version: string;
    last_update: number;
    expiry_date: string;
    type: string;
    comments: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (unixTimestamp: number): string => {
    if (!unixTimestamp || unixTimestamp === 0) return "—";
    return new Date(unixTimestamp * 1000).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

const formatExpiry = (dateStr: string): string => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric'
    });
};

// ── Shared Primitives ─────────────────────────────────────────────────────────
const StatusBadge = ({ status }: { status: string }) => {
    const s = status?.toLowerCase() || "unknown";
    let color = "text-slate-400 bg-slate-400/10 border border-slate-400/20";
    if (s.includes("valid") || s.includes("active") || s === "up") {
        color = "text-emerald-400 bg-emerald-400/10 border border-emerald-400/20";
    } else if (s.includes("no_support") || s.includes("expired") || s.includes("invalid")) {
        color = "text-rose-400 bg-rose-400/10 border border-rose-400/20";
    } else if (s.includes("warning") || s.includes("expiring")) {
        color = "text-amber-400 bg-amber-400/10 border border-amber-400/20";
    }

    return (
        <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-md gap-1 font-bold text-[9px] uppercase tracking-wider whitespace-nowrap ${color}`}>
            {status ? status.replace(/_/g, ' ') : "UNKNOWN"}
        </span>
    );
};

const TypeBadge = ({ type }: { type: string }) => {
    if (!type) return <span className="text-gray-200">—</span>;
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase bg-indigo-500/10 border-indigo-500/20 text-indigo-400 whitespace-nowrap`}>
            {type.replace(/_/g, ' ')}
        </span>
    );
};

const renderDetailRow = (label: string, value: any, color: string = "text-slate-200") => (
    <div key={label} className="flex justify-between items-center py-1 border-b border-slate-800/50 last:border-0 hover:bg-slate-800/30 px-1 -mx-1 rounded transition-colors">
        <span className={`text-[9px] font-bold ${color} uppercase tracking-widest`}>{label}</span>
        <span className={`text-[10px] font-black ${color} text-right max-w-[65%] truncate`} title={String(value ?? "")}>
            {value !== null && value !== undefined && value !== "" ? value : "—"}
        </span>
    </div>
);

// ── Detail View ───────────────────────────────────────────────────────────────
function LicenseDetailView({ item, onBack }: { item: FirewallLicenseItem; onBack: () => void }) {
    return (
        <div className="space-y-3 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 bg-gray-800/40 border border-gray-700 p-2.5 rounded-xl">
                <div className="flex items-center gap-2.5">
                    <button
                        onClick={onBack}
                        className="p-1.5 bg-indigo-500/10 border border-indigo-500/30 rounded-md text-indigo-400 hover:text-white hover:bg-indigo-500/20 transition-all group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                    </button>
                    <div className="bg-indigo-500/10 p-1.5 rounded-md border border-indigo-500/20">
                        <Key className="w-4 h-4 text-indigo-400" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-sm font-bold text-white leading-none">{item.name}</h2>
                            <StatusBadge status={item.status} />
                        </div>
                        <p className="text-[10px] text-gray-200 mt-0.5 font-mono">{item.key}</p>
                    </div>
                </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identity */}
                <div className="bg-gray-800/40 border border-gray-700 p-3 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-2 border-b border-gray-700 pb-1.5">
                        <Info className="w-3.5 h-3.5 text-blue-400" />
                        <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Identity & Type</h4>
                    </div>
                    <div className="space-y-0.5">
                        {renderDetailRow("Name", item.name)}
                        {renderDetailRow("License Key", item.key)}
                        {renderDetailRow("Firewall Type", item.firewall_type.toUpperCase())}
                        {renderDetailRow("Category Type", item.type ? item.type.replace(/_/g, ' ') : "—")}
                    </div>
                </div>

                {/* Properties */}
                <div className="bg-gray-800/40 border border-gray-700 p-3 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-2 border-b border-gray-700 pb-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                        <h4 className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Properties</h4>
                    </div>
                    <div className="space-y-0.5">
                        {renderDetailRow("Status", item.status.replace(/_/g, ' ').toUpperCase(), item.status === "no_support" ? "text-rose-400" : "text-emerald-400")}
                        {renderDetailRow("Entitlement", item.entitlement)}
                        {renderDetailRow("Version", item.version)}
                    </div>
                </div>

                {/* Timeline */}
                <div className="bg-gray-800/40 border border-gray-700 p-3 rounded-lg">
                    <div className="flex items-center gap-1.5 mb-2 border-b border-gray-700 pb-1.5">
                        <Clock className="w-3.5 h-3.5 text-orange-400" />
                        <h4 className="text-[10px] font-black text-orange-400 uppercase tracking-widest">Timeline</h4>
                    </div>
                    <div className="space-y-0.5">
                        {renderDetailRow("Last Update", formatDate(item.last_update))}
                        {renderDetailRow("Expiry Date", formatExpiry(item.expiry_date))}
                        {renderDetailRow("Record Created", new Date(item.created).toLocaleDateString())}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallLicenses({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [licenses, setLicenses] = useState<FirewallLicenseItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selected, setSelected] = useState<FirewallLicenseItem | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchLicenses = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) {
                queryObj.$or = [
                    { name: { $ilike: `%${debouncedSearch}%` } },
                    { key: { $ilike: `%${debouncedSearch}%` } }
                ];
            }
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const res = await baseapi.get("/firewall_licenses", { params });
            if (res.data.status) {
                setLicenses(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else {
                showError("Failed to load licenses");
            }
        } catch (e: any) {
            showError(e.message);
        } finally {
            setLoading(false);
        }
    }, [asset_id, debouncedSearch, currentPage, limit, sortConfig]);

    useEffect(() => {
        if (!selected) fetchLicenses();
    }, [fetchLicenses, selected]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, right = false }: { label: string; sortKey: string; right?: boolean }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className={`px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer group select-none ${right ? "text-right" : ""}`}
            >
                <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-indigo-400" />
                            ) : (
                                <ChevronDown size={12} className="text-indigo-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    // ── Detail View ────────────────────────────────────────────────────────────
    if (selected) {
        return <LicenseDetailView item={selected} onBack={() => setSelected(null)} />;
    }

    // ── Table View ─────────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col bg-transparent text-gray-200">

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-600/20 p-1.5 rounded-lg border border-indigo-500/30">
                        <Key size={16} className="text-indigo-400" />
                    </div>
                    <h1 className="text-lg font-bold text-white tracking-wide">Licenses & Services</h1>
                </div>

                <div className="flex items-center gap-2 ml-auto">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-indigo-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search licenses..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 transition-all"
                        />
                    </div>
                    <button
                        onClick={fetchLicenses}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0 disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-indigo-400"} />
                    </button>
                </div>
            </div>

            {/* Table Area Container */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                <SortableHeader label="Name & Key" sortKey="name" />
                                <SortableHeader label="Status" sortKey="status" />
                                <SortableHeader label="Type" sortKey="type" />
                                <SortableHeader label="Entitlement" sortKey="entitlement" />
                                <SortableHeader label="Version" sortKey="version" />
                                <SortableHeader label="Last Update" sortKey="last_update" />
                                <SortableHeader label="Expiry Date" sortKey="expiry_date" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-200">
                                            <Loader2 className="w-4 h-4 animate-spin text-indigo-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">Loading licenses...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : licenses.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <Key size={24} className="text-gray-200" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">No licenses found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                licenses.map((item) => {
                                    return (
                                        <tr
                                            key={item.id}
                                            onClick={() => setSelected(item)}
                                            className="hover:bg-gray-700/50 transition-colors cursor-pointer"
                                            title="Click to view details"
                                        >
                                            {/* Name & Key */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="text-[11px] font-bold text-gray-100">{item.name}</span>
                                                </div>
                                            </td>

                                            {/* Status */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <StatusBadge status={item.status} />
                                            </td>

                                            {/* Type */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <TypeBadge type={item.type} />
                                            </td>

                                            {/* Entitlement */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                {item.entitlement ? (
                                                    <span className="text-[10px] font-bold text-gray-300 bg-gray-700/50 px-1.5 py-0.5 rounded border border-gray-600/50">
                                                        {item.entitlement}
                                                    </span>
                                                ) : <span className="text-gray-200">—</span>}
                                            </td>

                                            {/* Version */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                {item.version ? (
                                                    <span className="text-[10px] text-gray-200 font-mono">{item.version}</span>
                                                ) : <span className="text-gray-200">—</span>}
                                            </td>

                                            {/* Last Update */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5 text-[10px] text-gray-200">
                                                    {item.last_update > 0 && <Clock size={10} className="text-gray-200" />}
                                                    {formatDate(item.last_update)}
                                                </div>
                                            </td>

                                            {/* Expiry Date */}
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5 text-[10px] text-gray-200">
                                                    {item.expiry_date && <Calendar size={10} className="text-gray-200" />}
                                                    {formatExpiry(item.expiry_date)}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-gray-200 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-200">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-indigo-400 hover:text-indigo-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-gray-200 hover:text-white disabled:opacity-20 bg-gray-800/50 hover:bg-gray-700 rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}